import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Container, Col, Row, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './SupplierSignup.css';
import { checkIsLinkExpired, registerSupplier } from '../Supplier/store/Supplier.action';
import Loader from '../../Components/Utility/Loader/Loader';
import logo from '../../assets/images/bombiix-logo.svg';

function SupplierSignup() {
  const [supplierInfo, setSupplierInfo] = useState({
    firstName: '',
    lastName: '',
    newPassword: '',
    confirmPassword: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  // State for password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const history = useHistory();
  const DATA_FILLED = 'data-filled';

  // Toggle functions for password visibility
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    const getSearchParams = () => {
      const srchParms = new URLSearchParams(window?.location?.search);
      const email = decodeURIComponent(srchParms.get('email'));
      const code = srchParms.get('code');
      const id = srchParms.get('id');
      const company = srchParms.get('company');
      setSearchParams({ email, code, id, company });
    };
    getSearchParams();
  }, []);

  useEffect(() => {
    if (searchParams.email) {
      setSupplierInfo((prev) => ({ ...prev, email: searchParams.email }));
    }
  }, [searchParams.email]);

  useEffect(() => {
    const fetch = async () => {
      const { code, id, company } = searchParams;
      if (code && id && company) {
        const obj = { code, supplierId: id, companyName: company };
        const res = await dispatch(checkIsLinkExpired(obj));
        setIsLinkExpired(!res || Boolean(res.verified || res.tokenMismatched || res.deleted));
      } else {
        setIsLinkExpired(true);
      }
    };
    fetch();
  }, [searchParams, dispatch]);

  const validatePassword = (pass) => {
    const regex = /^(?=.*[A-Z])(?=.*[^a-zA-Z]).+$/;
    return regex.exec(pass);
  };

  const validateForm = (key, val) => {
    const keywords = {
      firstName: t('SUPPLIER_SIGNUP.FIRST_NAME_LABEL'),
      lastName: t('SUPPLIER_SIGNUP.LAST_NAME_LABEL'),
      newPassword: t('SUPPLIER_SIGNUP.PASSWORD_LABEL'),
      confirmPassword: t('SUPPLIER_SIGNUP.RETYPE_PASSWORD'),
    };
    if (!val || val.trim().length === 0) {
      setErrors((prev) => ({ ...prev, [key]: `${keywords[key]} is required` }));
      return false;
    }
    if (key === 'newPassword') {
      if (!errors.confirmPassword && val !== supplierInfo.confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: t('SUPPLIER_SIGNUP.PASSWORD_RETYPE_PASSWORD_NOT_MATCH'),
        }));
      }
      if (!validatePassword(val)) {
        setErrors((prev) => ({ ...prev, [key]: t('SUPPLIER_SIGNUP.PASSWORD_REQUIREMENTS') }));
        return false;
      }
    }
    if (key === 'confirmPassword') {
      if (!validatePassword(val)) {
        setErrors((prev) => ({ ...prev, [key]: t('SUPPLIER_SIGNUP.PASSWORD_REQUIREMENTS') }));
        return false;
      }
      if (val !== supplierInfo.newPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: t('SUPPLIER_SIGNUP.PASSWORD_RETYPE_PASSWORD_NOT_MATCH'),
        }));
        return false;
      }
    }
    setErrors((prev) => {
      const updateErr = { ...prev };
      delete updateErr[key];
      return updateErr;
    });
    return true;
  };

  const postAPI = async () => {
    const { id, code, email, company } = searchParams;
    if (!id || !code || !email || !company) return;
    try {
      const obj = { ...supplierInfo };
      const params = { code, id, company };
      delete obj.email;
      obj.userId = id;
      const res = await dispatch(registerSupplier(obj, params));
      if (res.redirectToLogin) {
        history.push(`/setup2fa?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSupplierInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    validateForm(name, value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitted(true);

    const isInvalid = Object.entries(supplierInfo).reduce((acc, obj) => {
      if (!validateForm(obj[0], obj[1])) {
        return true;
      }
      return acc;
    }, false);
    if (isInvalid || Object.values(errors).length) return;
    await postAPI();
  };

  if (isLinkExpired === null) {
    return <Loader />;
  }

  return (
    <Container>
      <div className="login-container-bombiix supplier-signup-container">
        <div className="logo-container text-center">
          <img src={logo} className="img-fluid logo-image" alt="logo" />
        </div>
        {isLinkExpired ? (
          <p className="link-expired-text">{t('SUPPLIER_SIGNUP.ALERT_LINK_EXPIRED')}</p>
        ) : (
          <div className="mt-40">
            <p className="text-center mb-4 text-medium">{t('SUPPLIER_SIGNUP.SIGN_UP_TITLE')}</p>

            <Form name="frmRegisterSupplier" onSubmit={handleSubmit}>
              <Row>
                <Col md={6} sm={6}>
                  <Form.Group>
                    <span className="fix-icon icon-user" />
                    <Form.Control type="text" name="userType" value="Supplier" onChange={handleChange} disabled />
                  </Form.Group>
                </Col>
                <Col md={6} sm={6}>
                  <Form.Group>
                    <span className="fix-icon icon-email" />
                    <Form.Control type="email" name="email" title={supplierInfo.email} value={supplierInfo.email} onChange={handleChange} disabled />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6} sm={6}>
                  <Form.Group>
                    <Form.Label className="form-label">
                      {t('SUPPLIER_SIGNUP.FIRST_NAME_LABEL')}
                      <span className="required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={supplierInfo.firstName}
                      onChange={handleChange}
                      placeholder={t('SUPPLIER_SIGNUP.ENTER_NAME')}
                      className={supplierInfo.firstName && DATA_FILLED}
                    />

                    {isSubmitted && <Form.Text className="text-danger">{errors.firstName}</Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={6} sm={6}>
                  <Form.Group>
                    <Form.Label className="form-label">
                      {t('SUPPLIER_SIGNUP.LAST_NAME_LABEL')}
                      <span className="required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={supplierInfo.lastName}
                      onChange={handleChange}
                      placeholder={t('SUPPLIER_SIGNUP.ENTER_NAME')}
                      className={supplierInfo.lastName && DATA_FILLED}
                    />
                    {isSubmitted && <Form.Text className="text-danger">{errors.lastName}</Form.Text>}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} sm={6}>
                  <Form.Group className="position-relative">
                    <Form.Label className="form-label">
                      {t('SUPPLIER_SIGNUP.PASSWORD_LABEL')}
                      <span className="required">*</span>
                    </Form.Label>
                    <div className="password-input-container">
                      <Form.Control
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={supplierInfo.newPassword}
                        onChange={handleChange}
                        className={`password-input ${supplierInfo.newPassword && DATA_FILLED}`}
                        placeholder={t('SUPPLIER_SIGNUP.ENTER_PASSWORD')}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={toggleNewPasswordVisibility}
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        <i className={`fas ${showNewPassword ? 'icon-Password-Eye-Open' : 'icon-Password-Eye-Close'}`} />
                      </button>
                    </div>
                    {isSubmitted && <Form.Text className="text-danger">{errors.newPassword}</Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={6} sm={6}>
                  <Form.Group className="position-relative">
                    <Form.Label className="form-label">
                      {t('SUPPLIER_SIGNUP.CONFIRM_PASSWORD_LABEL')}
                      <span className="required">*</span>
                    </Form.Label>
                    <div className="password-input-container">
                      <Form.Control
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={supplierInfo.confirmPassword}
                        onChange={handleChange}
                        className={`password-input ${supplierInfo.confirmPassword && DATA_FILLED}`}
                        placeholder={t('SUPPLIER_SIGNUP.CONFIRM_PASSWORD')}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={toggleConfirmPasswordVisibility}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        <i className={`fas ${showConfirmPassword ? 'icon-Password-Eye-Open' : 'icon-Password-Eye-Close'}`} />
                      </button>
                    </div>
                    {isSubmitted && <Form.Text className="text-danger">{errors.confirmPassword}</Form.Text>}
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-center align-items-center">
                <Button type="submit" className="btn-theme">
                  {t('SUPPLIER_SIGNUP.SUBMIT_BUTTON')}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </div>
    </Container>
  );
}

export default SupplierSignup;
